import { useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { safeRedirect, consumeStoredRedirect } from './utils/redirect';
import { LoginPage } from './pages/LoginPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { LaunchCallbackPage } from './pages/LaunchCallbackPage';
import { ConnectLetzplayPage } from './pages/ConnectLetzplayPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { HubPage } from './pages/HubPage';
import { StatsPage } from './pages/StatsPage';
import { LandingPage } from './pages/LandingPage';
import { SettingsPage } from './pages/SettingsPage';
import { RankingsPage } from './pages/RankingsPage';
import { RankingDetailPage } from './pages/RankingDetailPage';
import { RankingRoundPage } from './pages/RankingRoundPage';
import { JoinRankingPage } from './pages/JoinRankingPage';
import { CompetitionsPage } from './pages/CompetitionsPage';
import { CompetitionDetailPage } from './pages/CompetitionDetailPage';
import { JoinCompetitionPage } from './pages/JoinCompetitionPage';
import { ResetPage } from './pages/ResetPage';
import { SupportPage } from './pages/SupportPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { ShareCardPlaygroundPage } from './pages/ShareCardPlaygroundPage';
import { BarRacePlaygroundPage } from './pages/BarRacePlaygroundPage';
import { RankingEvolutionCapturePage } from './pages/RankingEvolutionCapturePage';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { UpdateBanner } from './components/common/UpdateBanner';
import { InstallBanner } from './components/common/InstallBanner';
import { MigrationBanner } from './components/common/MigrationBanner';
import { PushSubscriptionSync } from './components/common/PushSubscriptionSync';
import { isLegacyOrigin } from './utils/canonicalOrigin';
import { isStandalone } from './utils/standalone';
import { usePwaTracking } from './hooks/usePwaTracking';

// Protected route wrapper — bounces visitors to /login, keeping the deep link.
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const profileGate = useProfileGate();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-deep-blue flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the attempted destination (path + query) so the user lands back
    // here after logging in — e.g. deep links from notification emails.
    const target = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(target)}`}
        replace
        state={location.state}
      />
    );
  }

  if (profileGate) return profileGate;

  return <>{children}</>;
}

/**
 * Signed-in accounts with no name go and set a profile up first. Magic-link and
 * Google signups both arrive nameless, and standings filter out nameless
 * players, so this is the one thing the app needs before you appear anywhere.
 *
 * Shared by every guard that renders a signed-in view. `/` is not behind
 * ProtectedRoute — it swaps between the landing page and the hub — so the check
 * has to live somewhere both can reach it.
 */
function useProfileGate(): React.ReactElement | null {
  const { subscriber } = useAuth();
  const location = useLocation();

  if (!subscriber || subscriber.name?.trim()) return null;

  const target = `${location.pathname}${location.search}`;
  return (
    <Navigate
      to={`/onboarding?redirect=${encodeURIComponent(target)}`}
      replace
      state={location.state}
    />
  );
}


// Auth route wrapper (redirect if already logged in)
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const destinationRef = useRef<string | null>(null);

  /**
   * Where to send someone who just signed in — resolved at most once.
   *
   * Signing in settles in two steps (session, then subscriber), so this render
   * runs again before the Navigate commits. Reading localStorage is
   * destructive, so the second pass used to find the key gone and fall back to
   * "/" + the raw query — landing on the hub at "/?redirect=/rankings/join/XYZ"
   * with the deep link stranded in a param nothing reads.
   *
   * ?redirect= comes first because it survives re-reads; localStorage is the
   * fallback for the magic-link round trip, where the param is on the tab that
   * requested the link, not the one that opens it.
   */
  const resolveDestination = () => {
    if (destinationRef.current === null) {
      const param = new URLSearchParams(location.search).get('redirect');
      destinationRef.current = safeRedirect(param) ?? consumeStoredRedirect() ?? '/';
    }
    return destinationRef.current;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-deep-blue flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={resolveDestination()} replace state={location.state} />;
  }

  return <>{children}</>;
}

// Connect route wrapper. The LetzPlay import is voluntary now — already
// connected users land here to change their username, not get bounced away.
function ConnectRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-deep-blue flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={`/login${location.search}`} replace state={location.state} />;
  }

  return <>{children}</>;
}

/**
 * `/` — landing page for visitors, Início hub for signed-in users.
 *
 * Rendered in place rather than redirected: an installed PWA cold-starts here,
 * and a redirect would flash the marketing page on every launch. For the same
 * reason the auth-loading state is a splash, never the landing.
 *
 * The installed-and-signed-out case goes to `/login` instead of the landing
 * page. `start_url` is `/`, so a session that expired turns every cold start
 * into a pitch for an app the user has already installed — and the landing
 * page's own "Entrar" is then just an extra tap between them and the form they
 * actually needed. Query params come along because `?moved=1` arrives here.
 */
function HomeRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const profileGate = useProfileGate();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-deep-blue flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return isStandalone() ? (
      <Navigate to={`/login${location.search}`} replace state={location.state} />
    ) : (
      <LandingPage />
    );
  }
  if (profileGate) return profileGate;

  return <HubPage />;
}

// Component to redirect while preserving query params
function RedirectWithQuery({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace state={location.state} />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Auth routes */}
      <Route
        path="/login"
        element={
          <AuthRoute>
            <LoginPage />
          </AuthRoute>
        }
      />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/launch" element={<LaunchCallbackPage />} />
      <Route path="/reset" element={<ResetPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/playground/share-card" element={<ShareCardPlaygroundPage />} />
      <Route path="/playground/bar-race" element={<BarRacePlaygroundPage />} />
      <Route path="/capture/ranking-evolution" element={<RankingEvolutionCapturePage />} />

      {/* First-run profile setup */}
      <Route
        path="/onboarding"
        element={
          <ConnectRoute>
            <OnboardingPage />
          </ConnectRoute>
        }
      />

      {/* Connect Letzplay */}
      <Route
        path="/connect"
        element={
          <ConnectRoute>
            <ConnectLetzplayPage />
          </ConnectRoute>
        }
      />

      {/* Protected routes */}
      {/* /dashboard is retired but kept as a permanent redirect: old bookmarks,
          notification emails and stale PWA bundles still point at it. */}
      <Route path="/dashboard" element={<RedirectWithQuery to="/" />} />
      <Route
        path="/stats"
        element={
          <ProtectedRoute>
            <StatsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rankings"
        element={
          <ProtectedRoute>
            <RankingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rankings/:rankingId"
        element={
          <ProtectedRoute>
            <RankingDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rankings/:rankingId/rounds/:roundId"
        element={
          <ProtectedRoute>
            <RankingRoundPage />
          </ProtectedRoute>
        }
      />
      <Route path="/rankings/join/:inviteCode" element={<JoinRankingPage />} />
      <Route
        path="/tournaments"
        element={
          <ProtectedRoute>
            <CompetitionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/:competitionId"
        element={
          <ProtectedRoute>
            <CompetitionDetailPage />
          </ProtectedRoute>
        }
      />
      <Route path="/tournaments/join/:inviteCode" element={<JoinCompetitionPage />} />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <SupportPage />
          </ProtectedRoute>
        }
      />

      {/* Home: landing (logged out) or Início hub (logged in) */}
      <Route path="/" element={<HomeRoute />} />
      <Route path="*" element={<RedirectWithQuery to="/" />} />
    </Routes>
  );
}

function PwaTracker() {
  usePwaTracking()
  return null
}

// Global overlays (update/install prompts, PWA tracking). Suppressed on the
// server-side capture route so they never get baked into the rendered video.
function GlobalOverlays() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  if (location.pathname.startsWith('/capture/')) return null;
  return (
    <>
      <UpdateBanner />
      {/* On the old origin the migration banner replaces the install prompt
          outright: inviting someone to install the domain we are retiring
          manufactures exactly the migration debt Phase 3 exists to pay off. */}
      {isLegacyOrigin() ? (
        <MigrationBanner />
      ) : (
        /* The install prompt dims the page behind it — never show it to a
           visitor still reading the landing page. Catching the browser event is
           separate and unconditional (see useInstallPrompt), so gating the UI
           here does not lose it. */
        isAuthenticated && <InstallBanner />
      )}
      <PwaTracker />
      <PushSubscriptionSync />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <GlobalOverlays />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
